import { QBox, QStack, QText } from '@qualio/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentCategory,
  QualioDocumentAuditGroups,
} from '../../../../../api/model/document';
import { Group } from '../../../../../api/model/group';
import { Tag } from '../../../../../api/model/tag';
import { MedtechUserV2 } from '../../../../../api/user';
import {
  isNormalUsergroup,
  isQualityUsergroup,
} from '../../../../../util/CurrentUser';
import { DocumentUsers } from '../DocumentUsers';
import {
  ManageApproversButton,
  ManageApproversButtonProps,
} from './ManageApproversButton';

export type ManageApproversProps = {
  approvers: QualioDocumentAuditGroups['approver_group'];
  groups: Group[];
  tags: Tag[];
  users: MedtechUserV2[];
  approverType: ManageApproversButtonProps['approverType'];
  documentCategory: DocumentCategory;
};

type ManageApproversWrapperProps = Pick<
  ManageApproversProps,
  'approvers' | 'tags' | 'groups' | 'users' | 'documentCategory'
>;

const ManageApprovers = ({
  approvers = [],
  groups,
  tags,
  approverType,
  users,
  documentCategory,
}: ManageApproversProps) => {
  const { t } = useTranslation();
  const titleMap: Record<ManageApproversProps['approverType'], string> = {
    quality: t('labels.qualityApprovers'),
    other: t('labels.otherApprovers'),
    mergedApprovers: t('labels.approvers'),
  };
  const modalTitleMap: Record<ManageApproversProps['approverType'], string> = {
    quality: t('labels.manageQualityApprovers'),
    other: t('labels.manageOtherApprovers'),
    mergedApprovers: t('labels.manageApprovers'),
  };
  const modalSubtitleMap: Record<ManageApproversProps['approverType'], string> =
    {
      quality: t('messages.selectUsersRequiredForApproval'),
      other: t('messages.selectUsersResponsibleForApproval'),
      mergedApprovers: t('messages.selectUsersResponsibleForApproval'),
    };
  const title = titleMap[approverType];
  const modalTitle = modalTitleMap[approverType];
  const modalSubtitle = modalSubtitleMap[approverType];
  return (
    <QBox data-cy={`document-${approverType}-approvers`}>
      <QStack
        isInline
        justifyContent="space-between"
        alignItems="center"
        data-cy={`document-manage-${approverType}-approvers`}
      >
        <QText fontWeight="semibold" fontSize="md">
          {title}
        </QText>
        <ManageApproversButton
          modalTitle={modalTitle}
          modalSubtitle={modalSubtitle}
          tags={tags}
          groups={groups}
          users={users}
          currentApprovers={approvers}
          approverType={approverType}
          documentCategory={documentCategory}
        />
      </QStack>
      <DocumentUsers userType="approver" users={approvers} />
    </QBox>
  );
};

export const ManageQualityApprovers = ({
  approvers,
  tags,
  groups,
  users,
  documentCategory,
}: ManageApproversWrapperProps) => {
  const currentApprovers = useMemo(
    () => approvers?.filter(isQualityUsergroup),
    [approvers],
  );
  const potentialApprovers = useMemo(
    () => users.filter(isQualityUsergroup),
    [users],
  );
  return (
    <ManageApprovers
      approverType="quality"
      approvers={currentApprovers}
      tags={tags}
      groups={groups}
      users={potentialApprovers}
      data-cy="document-manage-quality-approvers"
      documentCategory={documentCategory}
    />
  );
};

export const ManageOtherApprovers = ({
  approvers,
  tags,
  groups,
  users,
  documentCategory,
}: ManageApproversWrapperProps) => {
  const currentApprovers = useMemo(
    () => approvers?.filter((user) => !isQualityUsergroup(user)),
    [approvers],
  );
  const potentialApprovers = useMemo(
    () => users.filter(isNormalUsergroup),
    [users],
  );
  return (
    <ManageApprovers
      approverType="other"
      approvers={currentApprovers}
      tags={tags}
      groups={groups}
      users={potentialApprovers}
      documentCategory={documentCategory}
    />
  );
};

export const ManageMergedApprovers = ({
  approvers,
  tags,
  groups,
  users,
  documentCategory,
}: ManageApproversWrapperProps) => {
  const potentialApprovers = useMemo(() => {
    const byId = new Map<MedtechUserV2['id'], MedtechUserV2>();
    users
      .filter((user) => isQualityUsergroup(user) || isNormalUsergroup(user))
      .forEach((user) => byId.set(user.id, user));
    return Array.from(byId.values());
  }, [users]);
  return (
    <ManageApprovers
      approverType="mergedApprovers"
      approvers={approvers}
      tags={tags}
      groups={groups}
      users={potentialApprovers}
      documentCategory={documentCategory}
    />
  );
};
